import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut,
  User,
  Mail,
  Phone,
  Building2,
  Bell,
  BellOff,
  ArrowLeft,
  Inbox,
  Loader2,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LOGO_URL } from "../lib/constants";
import { toast } from "sonner";

export default function CustomerProfile() {
  const { user, logout, updateProfile } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    company: "",
    marketing_opt_in: true,
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [loadingQuotes, setLoadingQuotes] = useState(true);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        company: user.company || "",
        marketing_opt_in: user.marketing_opt_in ?? true,
      });
    }
  }, [user]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/api/auth/me/quotations");
        if (alive) setQuotes(data || []);
      } catch (err) {
        if (alive) toast.error("Error al cargar cotizaciones", {
          description: formatApiError(err),
        });
      } finally {
        if (alive) setLoadingQuotes(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    const res = await updateProfile(form);
    setSavingProfile(false);
    if (res.ok) toast.success("Perfil actualizado");
    else toast.error("Error al actualizar", { description: res.error });
  };

  const onLogout = () => {
    logout();
    nav("/", { replace: true });
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Pino Verde" className="h-9 w-9" />
            <div>
              <div className="font-display font-extrabold text-pv-green tracking-tight">
                MI CUENTA
              </div>
              <div className="text-xs text-slate-500">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="outline"
              size="sm"
              className="rounded-sm"
              data-testid="back-to-site"
            >
              <Link to="/">
                <ArrowLeft size={14} className="mr-1" /> Ver sitio
              </Link>
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              size="sm"
              data-testid="profile-logout"
              className="rounded-sm"
            >
              <LogOut size={14} className="mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 lg:px-8 py-8 grid lg:grid-cols-5 gap-8">
        {/* Profile form */}
        <section className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
          <div className="overline text-pv-orange mb-2">Perfil</div>
          <h2 className="font-display font-bold text-pv-green text-2xl mb-5">
            Mis datos
          </h2>
          <form onSubmit={onSave} className="space-y-4" data-testid="profile-form">
            <div>
              <Label className="text-slate-700">Nombre</Label>
              <div className="relative mt-1.5">
                <User
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  data-testid="profile-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="pl-9 h-11 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Correo</Label>
              <div className="relative mt-1.5">
                <Mail
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  value={user.email}
                  disabled
                  className="pl-9 h-11 rounded-sm border-slate-300 bg-slate-50 text-slate-500"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">
                El correo no se puede cambiar.
              </p>
            </div>
            <div>
              <Label className="text-slate-700">Teléfono</Label>
              <div className="relative mt-1.5">
                <Phone
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  data-testid="profile-phone"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="pl-9 h-11 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-700">Empresa</Label>
              <div className="relative mt-1.5">
                <Building2
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <Input
                  data-testid="profile-company"
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                  className="pl-9 h-11 rounded-sm border-slate-300 focus-visible:ring-pv-green"
                />
              </div>
            </div>

            <div
              className={`flex items-start gap-3 p-3 rounded-sm border cursor-pointer ${
                form.marketing_opt_in
                  ? "border-pv-lime bg-pv-lime/10"
                  : "border-slate-200 bg-slate-50"
              }`}
              onClick={() =>
                setForm((f) => ({ ...f, marketing_opt_in: !f.marketing_opt_in }))
              }
              data-testid="optin-toggle"
            >
              {form.marketing_opt_in ? (
                <Bell size={18} className="text-pv-green shrink-0 mt-0.5" />
              ) : (
                <BellOff size={18} className="text-slate-400 shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800">
                  {form.marketing_opt_in
                    ? "Recibiendo ofertas por correo"
                    : "No recibe ofertas"}
                </div>
                <div className="text-xs text-slate-500">
                  {form.marketing_opt_in
                    ? "Le enviaremos novedades, ofertas y nuevos productos."
                    : "No recibirá correos promocionales. Puede activarlo cuando quiera."}
                </div>
              </div>
              <input
                type="checkbox"
                checked={form.marketing_opt_in}
                onChange={() => {}}
                className="h-4 w-4 mt-1 accent-pv-green"
                tabIndex={-1}
              />
            </div>

            <Button
              type="submit"
              disabled={savingProfile}
              data-testid="profile-save"
              className="w-full h-11 rounded-sm bg-pv-green hover:bg-pv-green/90 text-white font-semibold"
            >
              {savingProfile ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" /> Guardando...
                </>
              ) : (
                "Guardar cambios"
              )}
            </Button>
          </form>
        </section>

        {/* Quotations */}
        <section className="lg:col-span-3 bg-white border border-slate-200 rounded-sm p-6">
          <div className="overline text-pv-orange mb-2">Mis cotizaciones</div>
          <h2 className="font-display font-bold text-pv-green text-2xl mb-5">
            Solicitudes enviadas
          </h2>

          {loadingQuotes ? (
            <div className="py-10 text-center text-slate-400">
              <Loader2 size={28} className="mx-auto animate-spin mb-2" />
              Cargando...
            </div>
          ) : quotes.length === 0 ? (
            <div className="py-12 text-center">
              <div className="inline-flex w-14 h-14 items-center justify-center bg-slate-100 rounded-sm mb-4">
                <Inbox className="text-slate-400" size={26} />
              </div>
              <h3 className="font-display font-bold text-pv-green text-lg mb-1">
                Aún no ha solicitado cotizaciones
              </h3>
              <p className="text-sm text-slate-500 mb-5 max-w-sm mx-auto">
                Cuando solicite una cotización desde el sitio, aparecerá aquí
                para hacerle seguimiento.
              </p>
              <Button
                asChild
                className="rounded-sm bg-pv-orange hover:bg-pv-orange/90 text-white font-semibold"
              >
                <Link to="/#contacto">Solicitar primera cotización</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="my-quotes-list">
              {quotes.map((q) => (
                <div
                  key={q.id}
                  className="border border-slate-200 rounded-sm p-4 hover:border-pv-lime transition-colors"
                  data-testid={`quote-${q.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-xs text-slate-400">
                      {new Date(q.created_at).toLocaleDateString("es-CR", {
                        day: "2-digit",
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                    <span className="px-2 py-0.5 text-xs bg-pv-lime/20 text-pv-green rounded-sm font-semibold">
                      {q.status === "nuevo"
                        ? "En revisión"
                        : q.status}
                    </span>
                  </div>
                  {q.products && (
                    <div className="text-sm font-semibold text-slate-800 mb-1">
                      {q.products}
                    </div>
                  )}
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                    {q.message}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
